import { useCallback, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { PayPaymentDueAccountVM } from "@propertelligent/client-api";

// TODO(feature/plaid-attempt-tracking): idempotencyKey is a new optional backend field on
// PayPaymentDueAccountVM not yet published in @propertelligent/client-api. Once the client
// republishes, drop this interim type and use the generated PayPaymentDueAccountVM directly
// (same interim-local-type pattern as usePaymentConfirmationPoll.ts).
export type PayPaymentDueAccountWithKey = PayPaymentDueAccountVM & {
  idempotencyKey?: string;
};

export type NextIdempotencyKey = () => string;

export interface PaymentSubmitGuard {
  isSubmitting: boolean;
  /**
   * Runs `fn` unless a guarded run is already in flight (then returns undefined without
   * calling it). `fn` receives `nextKey`, which returns a fresh UUID per call -- call it once
   * per pay request (the backend rejects a key reused across different dues).
   */
  runGuarded: <T>(
    fn: (nextKey: NextIdempotencyKey) => Promise<T>,
  ) => Promise<T | undefined>;
}

const nextKey: NextIdempotencyKey = () => uuidv4();

export const usePaymentSubmitGuard = (): PaymentSubmitGuard => {
  const inFlightRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const runGuarded = useCallback(
    async <T>(
      fn: (key: NextIdempotencyKey) => Promise<T>,
    ): Promise<T | undefined> => {
      // Ref check is synchronous, so a second click in the same tick is blocked before
      // React re-renders the disabled state.
      if (inFlightRef.current) {
        return undefined;
      }
      inFlightRef.current = true;
      setIsSubmitting(true);
      try {
        return await fn(nextKey);
      } finally {
        inFlightRef.current = false;
        setIsSubmitting(false);
      }
    },
    [],
  );

  return { isSubmitting, runGuarded };
};
